import { useState, type FormEvent } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  fetchAccountConsoleModules,
  setPlatformConsoleModule,
} from "../data/console-modules";
import {
  fetchPlatformAccounts,
  isLiveContractAccount,
  platformRenameAccount,
  upsertAccountContractLock,
  type PlatformAccount,
  type RenamedPlatformAccount,
} from "../data/platform-cockpit";
import { useReadModel } from "../data/useReadModel";
import { ConsentPolicyPanel } from "./ConsentPolicyPanel";
import { DutyTrackingPolicyRow } from "./DutyTrackingPolicyRow";
import { PlatformMobileFlagControls } from "./PlatformMobileFlagControls";
import { PlatformMembersDesk } from "./PlatformMembersDesk";
import { PlatformProvisionDrawer } from "./PlatformProvisionDrawer";

type AccountDesk = "overview" | "members" | "capabilities";

// Platform cockpit (PRD-001P): founder controls for every tenant from one place. Pick an
// account, keep its identity pinned, then use Overview, Members, or Capabilities without the
// workspace switcher. Console modules, mobile flags, and contract freeze remain separate groups.
// Every write is a hosted RPC and the server remains authoritative.
export function PlatformAccountControls({
  client,
  timeZone,
  pollMs,
  canManage,
  canProvision = false,
  canRename = false,
  canManageMembers = false,
  canManageModules = false,
  canSupportAs = false,
  onSupportStarted = () => {},
}: {
  client: SupabaseClient;
  timeZone: string;
  pollMs?: number;
  canManage: boolean;
  canProvision?: boolean;
  canRename?: boolean;
  canManageMembers?: boolean;
  canManageModules?: boolean;
  canSupportAs?: boolean;
  onSupportStarted?: () => void;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel<PlatformAccount[]>(
    () => fetchPlatformAccounts(client),
    [client, reloadNonce],
    {},
  );
  const [accountNameOverrides, setAccountNameOverrides] = useState<Record<string, string>>({});
  const accounts = (data ?? []).map((account) => {
    const accountName = accountNameOverrides[account.accountId];
    return accountName === undefined ? account : { ...account, accountName };
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [activeDesk, setActiveDesk] = useState<AccountDesk>("overview");
  const selected = accounts.find((account) => account.accountId === selectedId) ?? accounts[0] ?? null;

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading platform accounts…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load platform accounts."}
      </p>
    );
  }
  return (
    <div className="cockpit">
      <div className="cockpit__toolbar">
        {selected !== null ? (
          <label className="cockpit__picker">
            <span className="cockpit__picker-label">Account</span>
            <select
              aria-label="Platform account"
              value={selected.accountId}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.accountName}
                  {isLiveContractAccount(account) ? " - LIVE CONTRACT" : ""}
                  {account.contractLocked ? " - Frozen" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="cockpit__empty-heading">
            <strong>Workspaces</strong>
            <span>No workspaces are visible yet.</span>
          </div>
        )}
        {canProvision ? (
          <button
            type="button"
            className="cockpit__new-workspace"
            onClick={() => setProvisionOpen(true)}
          >
            New workspace
          </button>
        ) : null}
      </div>

      {selected !== null ? (
        <>
          <AccountHeader
            key={selected.accountId}
            client={client}
            account={selected}
            canRename={canRename}
            onRenamed={(account) => {
              setAccountNameOverrides((current) => ({
                ...current,
                [account.accountId]: account.accountName,
              }));
            }}
          />
          <div className="cockpit-tabs" role="tablist" aria-label="Selected workspace desk">
            <button
              id="cockpit-overview-tab"
              type="button"
              role="tab"
              aria-selected={activeDesk === "overview"}
              aria-controls="cockpit-overview-panel"
              className={activeDesk === "overview" ? "cockpit-tab cockpit-tab--active" : "cockpit-tab"}
              onClick={() => setActiveDesk("overview")}
            >
              Overview
            </button>
            <button
              id="cockpit-members-tab"
              type="button"
              role="tab"
              aria-selected={activeDesk === "members"}
              aria-controls="cockpit-members-panel"
              className={activeDesk === "members" ? "cockpit-tab cockpit-tab--active" : "cockpit-tab"}
              onClick={() => setActiveDesk("members")}
            >
              Members
            </button>
            <button
              id="cockpit-capabilities-tab"
              type="button"
              role="tab"
              aria-selected={activeDesk === "capabilities"}
              aria-controls="cockpit-capabilities-panel"
              className={activeDesk === "capabilities" ? "cockpit-tab cockpit-tab--active" : "cockpit-tab"}
              onClick={() => setActiveDesk("capabilities")}
            >
              Capabilities
            </button>
          </div>

          {activeDesk === "overview" ? (
            <div
              id="cockpit-overview-panel"
              role="tabpanel"
              aria-labelledby="cockpit-overview-tab"
            >
              <section className="cockpit-section" aria-label="Duty tracking">
                <h3 className="cockpit-section__title">Duty tracking</h3>
                <DutyTrackingPolicyRow
                  client={client}
                  accountId={selected.accountId}
                  canView
                  canManage={canManage}
                  {...(pollMs !== undefined ? { pollMs } : {})}
                />
              </section>

              <section className="cockpit-section" aria-label="Consent policies">
                <h3 className="cockpit-section__title">Consent policies</h3>
                <ConsentPolicyPanel
                  client={client}
                  accountId={selected.accountId}
                  canView
                  canManage={canManage}
                  {...(pollMs !== undefined ? { pollMs } : {})}
                />
              </section>
            </div>
          ) : activeDesk === "members" ? (
            <div
              id="cockpit-members-panel"
              role="tabpanel"
              aria-labelledby="cockpit-members-tab"
            >
              <PlatformMembersDesk
                key={selected.accountId}
                client={client}
                accountId={selected.accountId}
                timeZone={timeZone}
                canManage={canManageMembers}
                canSupportAs={canSupportAs}
                onSupportStarted={onSupportStarted}
                {...(pollMs !== undefined ? { pollMs } : {})}
              />
            </div>
          ) : (
            <div
              id="cockpit-capabilities-panel"
              role="tabpanel"
              aria-labelledby="cockpit-capabilities-tab"
            >
              <PlatformCapabilitiesDesk
                key={selected.accountId}
                client={client}
                account={selected}
                canManagePolicies={canManage}
                canManageModules={canManageModules}
                onLockChanged={() => setReloadNonce((nonce) => nonce + 1)}
              />
            </div>
          )}
        </>
      ) : (
        <p className="surface__state" role="status">
          Create a workspace here, or ask a platform owner to verify your access.
        </p>
      )}

      {canProvision ? (
        <PlatformProvisionDrawer
          client={client}
          open={provisionOpen}
          onClose={() => setProvisionOpen(false)}
          onProvisioned={(account) => {
            setSelectedId(account.accountId);
            setActiveDesk("overview");
            setReloadNonce((nonce) => nonce + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function AccountHeader({
  client,
  account,
  canRename,
  onRenamed,
}: {
  client: SupabaseClient;
  account: PlatformAccount;
  canRename: boolean;
  onRenamed: (account: RenamedPlatformAccount) => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailure, setCopyFailure] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account.accountName);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function beginRename(): void {
    setName(account.accountName);
    setFailure(null);
    setSaved(false);
    setEditing(true);
  }

  function cancelRename(): void {
    setName(account.accountName);
    setFailure(null);
    setEditing(false);
  }

  async function submitRename(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (busy || !canRename) return;

    const trimmedName = name.trim();
    if (trimmedName.length === 0) {
      setFailure("Workspace name is required.");
      return;
    }
    if (trimmedName.length > 120) {
      setFailure("Workspace name must be 120 characters or fewer.");
      return;
    }

    setBusy(true);
    setFailure(null);
    const outcome = await platformRenameAccount(client, account.accountId, trimmedName);
    if (outcome.ok) {
      setName(outcome.account.accountName);
      setEditing(false);
      setSaved(true);
      onRenamed(outcome.account);
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  async function copyWorkspaceId(): Promise<void> {
    setCopyFailure(false);
    try {
      if (typeof navigator.clipboard?.writeText !== "function") {
        throw new Error("Clipboard unavailable");
      }
      await navigator.clipboard.writeText(account.accountId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopyFailure(true);
    }
  }

  return (
    <div className="cockpit-head">
      <div className="cockpit-head__identity">
        <div className="cockpit-head__name-row">
          {editing && canRename ? (
            <form
              className="cockpit-head__rename"
              aria-label="Rename workspace"
              onSubmit={(event) => void submitRename(event)}
            >
              <label className="tpl__field cockpit-head__rename-field">
                <span>Workspace name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={120}
                  autoComplete="organization"
                  required
                  autoFocus
                  disabled={busy}
                />
              </label>
              <div className="cockpit-head__rename-actions">
                <button type="submit" className="minter__confirm-go" disabled={busy}>
                  {busy ? "Saving..." : "Save name"}
                </button>
                <button type="button" className="builder__navbtn" disabled={busy} onClick={cancelRename}>
                  Cancel
                </button>
              </div>
              {failure !== null ? (
                <p className="surface__state surface__state--error" role="alert">{failure}</p>
              ) : null}
            </form>
          ) : (
            <>
              <span className="cockpit-head__name">{account.accountName}</span>
              {canRename ? (
                <button type="button" className="pfd-copy cockpit-head__rename-trigger" onClick={beginRename}>
                  Rename workspace
                </button>
              ) : null}
            </>
          )}
        </div>
        {saved ? <span className="cockpit-head__rename-status" role="status">Workspace name updated.</span> : null}
        <span className="cockpit-head__workspace-id">
          <span>Workspace id</span>
          <code>{account.accountId}</code>
          <button
            type="button"
            className="pfd-copy"
            onClick={() => void copyWorkspaceId()}
          >
            {copied ? "Copied" : "Copy"}
          </button>
          {copyFailure ? <span role="status">Select the id to copy it.</span> : null}
        </span>
        {isLiveContractAccount(account) ? (
          <span className="cockpit-badge cockpit-badge--live">LIVE CONTRACT</span>
        ) : null}
        {account.contractLocked ? (
          <span className="cockpit-badge cockpit-badge--locked">Frozen</span>
        ) : null}
      </div>
    </div>
  );
}

function PlatformCapabilitiesDesk({
  client,
  account,
  canManagePolicies,
  canManageModules,
  onLockChanged,
}: {
  client: SupabaseClient;
  account: PlatformAccount;
  canManagePolicies: boolean;
  canManageModules: boolean;
  onLockChanged: () => void;
}) {
  return (
    <div className="cockpit-capabilities">
      <ConsoleModulesControl
        client={client}
        account={account}
        canManage={canManageModules}
      />

      <section className="cockpit-capability" aria-label="Mobile flags">
        <div className="cockpit-capability__head">
          <div>
            <h3>Mobile flags</h3>
            <p>Controls how the Guard app behaves for this workspace.</p>
          </div>
        </div>
        <PlatformMobileFlagControls
          client={client}
          accountId={account.accountId}
          canManage={canManagePolicies}
        />
      </section>

      <ContractFreezeControl
        client={client}
        account={account}
        canManage={canManagePolicies}
        onLockChanged={onLockChanged}
      />
    </div>
  );
}

function ConsoleModulesControl({
  client,
  account,
  canManage,
}: {
  client: SupabaseClient;
  account: PlatformAccount;
  canManage: boolean;
}) {
  const [reloadNonce, setReloadNonce] = useState(0);
  const { status, data, error } = useReadModel(
    () => fetchAccountConsoleModules(client, account.accountId),
    [client, account.accountId, reloadNonce],
    {},
  );
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function save(enabled: boolean): Promise<void> {
    if (busy || !canManage) return;
    setBusy(true);
    setFailure(null);
    const outcome = await setPlatformConsoleModule(
      client,
      account.accountId,
      "admin_branding",
      enabled,
    );
    if (outcome.ok) {
      setConfirmingOff(false);
      setReloadNonce((nonce) => nonce + 1);
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  const brandingEnabled = data?.adminBranding ?? false;
  return (
    <section className="cockpit-capability" aria-label="Console modules">
      <div className="cockpit-capability__head">
        <div>
          <h3>Console modules</h3>
          <p>Controls which Admin surfaces this workspace is offered.</p>
        </div>
      </div>

      {status === "loading" && data === null ? (
        <p className="surface__state" role="status">Loading Console modules...</p>
      ) : status === "error" ? (
        <p className="surface__state surface__state--error" role="alert">
          {error ?? "Could not load Console modules."}
        </p>
      ) : (
        <div className="cockpit-module-row">
          <span className="cockpit-module-row__copy">
            <strong>Branding</strong>
            <code>admin_branding</code>
            <span>Logo, color, theme, and app-name controls in Admin.</span>
          </span>
          <button
            type="button"
            role="switch"
            aria-label="Branding Console module"
            aria-checked={brandingEnabled}
            className={`cockpit-module-switch${brandingEnabled ? " cockpit-module-switch--on" : ""}`}
            disabled={busy || !canManage}
            onClick={() => {
              if (brandingEnabled) {
                setConfirmingOff(true);
              } else {
                void save(true);
              }
            }}
          >
            <span aria-hidden="true" />
            {busy ? "Saving..." : brandingEnabled ? "On" : "Off"}
          </button>
        </div>
      )}

      {confirmingOff ? (
        <div className="cockpit-module-confirm" role="group" aria-label="Turn Branding off?">
          <strong>Turn Branding off?</strong>
          <p>
            Admin will stop listing Branding and its policy controls for {account.accountName}.
            Existing brand data stays retained, and the server will reject Branding writes.
          </p>
          <div className="cockpit-module-confirm__actions">
            <button type="button" className="builder__navbtn" disabled={busy} onClick={() => setConfirmingOff(false)}>
              Cancel
            </button>
            <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => void save(false)}>
              {busy ? "Saving..." : "Turn Branding off"}
            </button>
          </div>
        </div>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
    </section>
  );
}

function ContractFreezeControl({
  client,
  account,
  canManage,
  onLockChanged,
}: {
  client: SupabaseClient;
  account: PlatformAccount;
  canManage: boolean;
  onLockChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  async function toggleLock(): Promise<void> {
    if (busy || !canManage) return;
    setBusy(true);
    setFailure(null);
    const outcome = await upsertAccountContractLock(client, account.accountId, !account.contractLocked);
    if (outcome.ok) {
      onLockChanged();
    } else {
      setFailure(outcome.reason);
    }
    setBusy(false);
  }

  return (
    <section className="cockpit-capability" aria-label="Contract freeze">
      <div className="cockpit-capability__head">
        <div>
          <h3>Contract freeze</h3>
          <p>Controls who may change live mobile flags and duty policy right now.</p>
        </div>
        <span className={`cockpit-badge${account.contractLocked ? " cockpit-badge--locked" : ""}`}>
          {account.contractLocked ? "Frozen" : "Open"}
        </span>
      </div>
      {canManage ? (
        <div className="cockpit-freeze-control">
          <button
            type="button"
            className={`cockpit-lock__btn${account.contractLocked ? " cockpit-lock__btn--on" : ""}`}
            disabled={busy}
            aria-pressed={account.contractLocked}
            onClick={() => void toggleLock()}
          >
            {busy ? "Saving..." : account.contractLocked ? "Unfreeze contract" : "Protect live contract"}
          </button>
          <span>
            {account.contractLocked
              ? "Account admins cannot change flags or duty while frozen; you can override."
              : "Freeze to block account-admin flag and duty changes. Platform superadmin keeps override access."}
          </span>
        </div>
      ) : null}
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">{failure}</p>
      ) : null}
    </section>
  );
}
